import React, { useMemo } from "react";
import useThemeColors from "../organization/portal/useThemeColors";
import {alphaFromHex} from "../../util/colors";
import useTranslation from "../../i18n/useTranslation";

/**
 * Props:
 *  - title
 *  - onAdd
 *  - page, pages, onPageChange
 *  - items, renderItem
 *  - loading
 *
 * Optional:
 *  - subtitle
 *  - loadingOverlay (default true)
 *  - variant: "light" | "dark" (default "light")
 *  - className
 *  - addButtonProps
 *  - addButtonTitle (defaults to Add)
 *  - addActions (same shape used in PaginatedFlatTable)
 *  - headerContent
 */
export default function PaginatedCardList({
                                              title,
                                              onAdd,
                                              page,
                                              pages,
                                              onPageChange,
                                              items,
                                              renderItem,
                                              loading,

                                              subtitle,
                                              loadingOverlay = true,
                                              variant = "light",
                                              className = "",
                                              addButtonProps,
                                              addButtonTitle,
                                              addActions,
                                              headerContent,
                                          }) {
    const isDark = variant === "dark";

    // 🔹 Use org portal theme when available, otherwise fall back to global palette
    const colors = useThemeColors();
    const primary = colors.primary;
    const {t} = useTranslation();
    const resolvedAddButtonTitle = addButtonTitle || t('common.table.add');

    const scopeId = useMemo(() => {
        const safe = String(title || "cards")
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/(^-|-$)/g, "");
        return `kbola-pcl-${safe || "x"}`;
    }, [title]);

    const ui = useMemo(() => {

        const light = {
            wrap: {
                borderRadius: 18,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                background: "#fff",
                boxShadow: "0 18px 44px rgba(0,0,0,0.10)",
            },
            header: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "12px 14px",
                // 🔹 Header uses primary (org or default) with a soft gradient
                background: `linear-gradient(
                    135deg,
                    ${alphaFromHex(primary, 0.50)},
                    ${alphaFromHex(primary, 0.0)}
                )`,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
            },
            titleWrap: {
                display: "flex",
                alignItems: "center",
                gap: 10,
                minWidth: 0,
            },
            badge: {
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.03em",
                padding: "6px 10px",
                borderRadius: 999,
                // keep global accent for counts
                background: colors.primary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: colors.textOnPrimary,
                whiteSpace: "nowrap",
            },
            title: {
                margin: 0,
                fontSize: 14,
                fontWeight: 900,
                color: colors.textOnPrimary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            },
            sub: {
                margin: 0,
                fontSize: 12.5,
                fontWeight: 700,
                color: colors.textOnPrimary,
            },
            headerRight: {
                display: "flex",
                alignItems: "center",
                gap: 10,
                flex: "0 0 auto",
            },
            btn: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid rgba(0,0,0,0.12)",
                background: "#fff",
                color: "#14151a",
                cursor: "pointer",
            },
            btnPrimary: {
                background: primary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: colors.textOnPrimary,
            },

            body: { padding: 18 },
            grid: { margin: 0, rowGap: 16 },

            overlayWrap: {
                position: "relative",
                minHeight: 120,
            },
            overlay: {
                position: "absolute",
                inset: 0,
                background: "rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
                borderRadius: 14,
            },
            overlayText: { color: "rgba(20,21,26,0.55)", fontWeight: 800 },

            emptyCard: {
                borderRadius: 14,
                padding: 14,
                border: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.015)",
                textAlign: "center",
                color: "rgba(20,21,26,0.60)",
                fontWeight: 800,
            },

            footer: {
                padding: "12px 14px",
                borderTop: "1px solid rgba(0,0,0,0.06)",
                background: "#fff",
            },

            dropdownBtn: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                cursor: "pointer",
                background: colors.primary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: colors.textOnPrimary,
            },
        };

        const dark = {
            ...light,
            wrap: {
                ...light.wrap,
                border: "1px solid rgba(255,255,255,0.08)",
                background: "rgba(0,0,0,0.18)",
                boxShadow: "0 18px 44px rgba(0,0,0,0.30)",
            },
            header: {
                ...light.header,
                // 🔹 Dark variant header also based on primary
                background: `
                    radial-gradient(
                        1100px 380px at 10% 0%,
                        ${alphaFromHex(primary, 0.16)},
                        rgba(0,0,0,0) 55%
                    ),
                    linear-gradient(
                        180deg,
                        rgba(255,255,255,0.04),
                        rgba(0,0,0,0)
                    )
                `,
                borderBottom: "1px solid rgba(255,255,255,0.08)",
            },
            title: { ...light.title, color: "rgba(255,255,255,0.90)" },
            sub: { ...light.sub, color: "rgba(255,255,255,0.70)" },

            btn: {
                ...light.btn,
                border: "1px solid rgba(255,255,255,0.14)",
                background: "rgba(255,255,255,0.06)",
                color: "rgba(255,255,255,0.90)",
            },
            btnPrimary: {
                ...light.btnPrimary,
                background: colors.primary,
                border: "1px solid rgba(0,0,0,0.12)",
                color: colors.textOnPrimary,
            },

            overlay: { ...light.overlay, background: "rgba(0,0,0,0.35)" },
            overlayText: { color: "rgba(255,255,255,0.75)", fontWeight: 800 },

            emptyCard: {
                ...light.emptyCard,
                border: "1px solid rgba(255,255,255,0.08)",
                background: "rgba(255,255,255,0.04)",
                color: "rgba(255,255,255,0.72)",
            },

            footer: {
                ...light.footer,
                background: "rgba(0,0,0,0.10)",
                borderTop: "1px solid rgba(255,255,255,0.08)",
            },

            dropdownBtn: {
                ...light.dropdownBtn,
                background: colors.primary,
                border: "1px solid rgba(0,0,0,0.12)",
                color: colors.textOnPrimary,
            },
        };

        const base = isDark ? dark : light;

        return {
            ...base,
            // used for focus ring in the scoped CSS
            focusShadow: alphaFromHex(primary, 0.18),
        };
    }, [isDark, primary, colors.primary, colors.textOnPrimary]);

    const safeItems = Array.isArray(items) ? items : [];
    const count = safeItems.length;

    const canPrev = (page || 1) > 1;
    const canNext = (page || 1) < (pages || 1);

    const hasMultiActions = Array.isArray(addActions) && addActions.length > 0;

    const AddArea = () => {
        if (hasMultiActions) {
            return (
                <div style={ui.headerRight}>
                    <div className="btn-group">
                        <button
                            type="button"
                            className="dropdown-toggle"
                            data-toggle="dropdown"
                            aria-haspopup="true"
                            aria-expanded="false"
                            style={{ ...ui.dropdownBtn, opacity: loading ? 0.75 : 1 }}
                            disabled={loading}
                            title={t('common.table.add')}
                        >
                            <i className="fas fa-plus" />
                            {t('common.table.add')}
                            <span className="sr-only">Toggle Dropdown</span>
                        </button>

                        <div
                            className="dropdown-menu dropdown-menu-right kbola-add-menu"
                            role="menu"
                        >
                            {addActions.map((act, i) => (
                                <button
                                    key={`add-action-${i}`}
                                    className="dropdown-item kbola-add-item"
                                    type="button"
                                    onClick={(e) => act?.onClick?.(e)}
                                    {...(act?.props || {})}
                                >
                                    {act?.iconClass ? (
                                        <i className={`${act.iconClass} mr-2`} />
                                    ) : null}
                                    {act?.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            );
        }

        if (onAdd) {
            return (
                <div style={ui.headerRight}>
                    <button
                        type="button"
                        style={{ ...ui.btn, ...ui.btnPrimary, opacity: loading ? 0.75 : 1 }}
                        onClick={onAdd}
                        disabled={loading}
                        title={resolvedAddButtonTitle}
                        {...(addButtonProps || {})}
                    >
                        <i className="fas fa-plus" />
                        {resolvedAddButtonTitle}
                    </button>
                </div>
            );
        }

        return null;
    };

    // ✅ Same pagination visuals as FlatTable (inline)
    const Pager = () => (
        <div className="kbola-pcl-pager">
            <button
                type="button"
                className="kbola-pcl-pager-btn"
                style={{
                    opacity: canPrev ? 1 : 0.78,
                    borderColor: isDark ? "rgba(255,255,255,0.22)" : "rgba(0,0,0,0.16)",
                    background: isDark ? "rgba(255,255,255,0.09)" : "#fff",
                    color: isDark ? "rgba(255,255,255,0.96)" : "#14151a",
                }}
                onClick={() => canPrev && onPageChange(page - 1)}
                disabled={!canPrev || loading}
                title="Previous page"
            >
                <i className="fas fa-arrow-left" />
                {t('common.table.prev')}
            </button>

            <div
                className="kbola-pcl-pager-text"
                style={{
                    color: isDark ? "rgba(255,255,255,0.70)" : "rgba(20,21,26,0.70)",
                }}
            >
                {t('common.table.page')} {page || 1} {t('common.table.of')} {pages || 1}
            </div>

            <button
                type="button"
                className="kbola-pcl-pager-btn"
                style={{
                    opacity: canNext ? 1 : 0.78,
                    borderColor: isDark ? "rgba(255,255,255,0.22)" : "rgba(0,0,0,0.16)",
                    background: isDark ? "rgba(255,255,255,0.09)" : "#fff",
                    color: isDark ? "rgba(255,255,255,0.96)" : "#14151a",
                }}
                onClick={() => canNext && onPageChange(page + 1)}
                disabled={!canNext || loading}
                title="Next page"
            >
                {t('common.table.next')}
                <i className="fas fa-arrow-right" />
            </button>
        </div>
    );

    return (
        <div id={scopeId} style={ui.wrap} className={className}>
            {/* Header */}
            <div style={ui.header}>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        minWidth: 0,
                    }}
                >
                    <span style={ui.badge}>{count}</span>
                    <div style={{ minWidth: 0 }}>
                        <h3 style={ui.title} title={title}>
                            {title}
                        </h3>
                        <p style={ui.sub}>{subtitle || t('common.table.subtitle')}</p>
                    </div>
                </div>

                <div style={ui.headerRight}>
                    {headerContent}
                    <AddArea />
                </div>
            </div>

            {/* Body */}
            <div style={ui.body}>
                <div style={ui.overlayWrap}>
                    {loadingOverlay && loading ? (
                        <div style={ui.overlay}>
                            <div className="text-center">
                                <div
                                    className="spinner-border"
                                    role="status"
                                    aria-label="Loading..."
                                />
                                <div className="mt-2" style={ui.overlayText}>
                                    Loading…
                                </div>
                            </div>
                        </div>
                    ) : null}

                    {!loading && safeItems.length === 0 ? (
                        <div style={ui.emptyCard}>No records found</div>
                    ) : (
                        <div className="row" style={ui.grid}>
                            {safeItems.map(renderItem)}
                        </div>
                    )}
                </div>
            </div>

            {/* Footer / pagination */}
            <div style={ui.footer}>
                <Pager />
            </div>

            {/* Scoped styles (match FlatTable feel) */}
            <style>{`
        #${scopeId} button:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem ${ui.focusShadow};
        }

        #${scopeId} .kbola-pcl-pager{
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:6px;
        }

        #${scopeId} .kbola-pcl-pager-btn{
          border-radius: 12px;
          min-width: 0;
          flex: 1 1 0;
          justify-content: space-between;
          padding: 7px 8px;
          font-size: 11px;
          font-weight: 900;
          border: 1px solid rgba(0,0,0,0.12);
          background: #fff;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 5px;
          white-space: nowrap;
          transition: transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease;
        }

        #${scopeId} .kbola-pcl-pager-btn:not(:disabled):hover{
          transform: translateY(-1px);
          border-color: ${primary};
          box-shadow: 0 5px 14px rgba(20,21,26,0.10);
        }

        #${scopeId} .kbola-pcl-pager-text{
          flex: 0 0 auto;
          font-weight: 900;
          font-size: 11px;
          white-space: nowrap;
        }

        /* Add actions dropdown polish (same as FlatTable snippet) */
        #${scopeId} .kbola-add-menu{
          border-radius: 14px;
          border: 1px solid ${isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.10)"};
          box-shadow: 0 18px 44px rgba(0,0,0,0.18);
          padding: 6px;
          min-width: 220px;
          background: ${isDark ? "rgba(20,21,26,0.98)" : "#fff"};
        }
        #${scopeId} .kbola-add-item{
          border-radius: 12px;
          font-weight: 900;
          padding: 10px 10px;
          display: flex;
          align-items: center;
          color: ${isDark ? "rgba(255,255,255,0.88)" : "rgba(20,21,26,0.88)"};
        }
        #${scopeId} .kbola-add-item:hover{
          background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.22);
          color: #14151a;
        }

        #${scopeId} .kbola-pcl-pager{
          padding-top: 2px;
        }

        @media (max-width: 700px) {
          #${scopeId} > div:first-child { flex-wrap: wrap; }
          #${scopeId} > div:first-child > div:last-child { width: 100%; justify-content: flex-end; }
          #${scopeId} .kbola-pcl-pager { flex-wrap: wrap; justify-content: center; }
          #${scopeId} .kbola-pcl-pager-text { order: -1; width: 100%; text-align: center; }
        }

        /* FontAwesome safety inside scoped area */
        #${scopeId} i.fas,
        #${scopeId} i.far,
        #${scopeId} i.fal,
        #${scopeId} i.fa,
        #${scopeId} i[class^="fa-"],
        #${scopeId} i[class*=" fa-"] {
          font-family: "Font Awesome 5 Free" !important;
          font-weight: 900;
        }
        #${scopeId} i.fab {
          font-family: "Font Awesome 5 Brands" !important;
          font-weight: 400;
        }
      `}</style>
        </div>
    );
}
